import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";
import { AgentActionService } from "../../services/AgentActionService";
import { ApplicationService } from "../../services/ApplicationService";
import { Agent } from "../../model_gen/agent_base";
import { ApplicationAction } from "../../model_gen/application";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PlusIcon } from "lucide-react";

interface AddCapabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: number;
  currentAgent: Agent | null;
  onCapabilityAdded: () => void;
}

const AddCapabilityModal: React.FC<AddCapabilityModalProps> = ({
  isOpen,
  onClose,
  agentId,
  currentAgent,
  onCapabilityAdded,
}) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [selectedCapabilityType, setSelectedCapabilityType] =
    useState<string>("");
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [availableActions, setAvailableActions] = useState<ApplicationAction[]>(
    []
  );
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [modalLoading, setModalLoading] = useState(false);

  const handleModalClose = () => {
    setSelectedCapabilityType("");
    setSelectedItem("");
    setAvailableAgents([]);
    setAvailableActions([]);
    onClose();
  };

  // Load data based on capability type
  useEffect(() => {
    if (selectedCapabilityType && currentAgent) {
      setModalLoading(true);
      setSelectedItem("");

      if (selectedCapabilityType === "subagent") {
        // Load available agents for the same application
        AgentService.getAgentsByApplication(
          currentAgent.application_id,
          () => {},
          apiKey
        ).subscribe(
          (response: any) => {
            // Handle different response formats
            const agentsList = Array.isArray(response)
              ? response
              : response.agents || [];
            // Get existing sub-agent IDs
            const existingSubAgentIds = new Set(
              (currentAgent.sub_agents || []).map((subAgent) => subAgent.id)
            );
            // Filter out current agent and already added sub-agents
            const filteredAgents = agentsList.filter(
              (agent: Agent) =>
                agent.id !== agentId && !existingSubAgentIds.has(agent.id)
            );
            setAvailableAgents(filteredAgents);
            setModalLoading(false);
          },
          (error: any) => {
            console.error("Error loading agents:", error);
            setAvailableAgents([]);
            setModalLoading(false);
          }
        );
      } else if (
        selectedCapabilityType === "api_call" ||
        selectedCapabilityType === "llm_call"
      ) {
        // Load available actions of the same type
        ApplicationService.getApplicationActions(
          currentAgent.application_id,
          () => {},
          apiKey
        ).subscribe(
          (response: any) => {
            // Handle different response formats
            const actionsList = Array.isArray(response)
              ? response
              : response.actions || [];
            // Filter actions by type
            const filteredActions = actionsList.filter(
              (action: ApplicationAction) =>
                action.action_type === selectedCapabilityType
            );
            setAvailableActions(filteredActions);
            setModalLoading(false);
          },
          (error: any) => {
            console.error("Error loading actions:", error);
            setAvailableActions([]);
            setModalLoading(false);
          }
        );
      }
    }
  }, [selectedCapabilityType, currentAgent, agentId, apiKey]);

  const handleCapabilityTypeSelect = (type: string) => {
    if (!selectedItem) return;

    setModalLoading(true);

    if (type === "subagent") {
      // Add sub-agent relationship
      AgentService.addSubAgent(
        Number(agentId),
        Number(selectedItem),
        () => {},
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Sub-agent added successfully:", response);
          onCapabilityAdded();
          handleModalClose();
          setModalLoading(false);
        },
        (error: any) => {
          console.error("Error adding sub-agent:", error);
          setModalLoading(false);
        }
      );
    } else if (type === "api_call" || type === "llm_call") {
      // Find the selected application action
      const selectedApplicationAction = availableActions.find(
        (action) => action.id?.toString() === selectedItem
      );

      if (!selectedApplicationAction) {
        console.error("Selected application action not found");
        setModalLoading(false);
        return;
      }

      // Create a new agent action based on the application action
      const newAgentAction = {
        agent_id: Number(agentId),
        action_name: selectedApplicationAction.action_type || "Untitled Action",
        action_type: selectedApplicationAction.action_type,
        description: `Action based on ${selectedApplicationAction.action_type}`,
        status: "ENABLED",
        is_default: false,
        action_result_type: "gather_info",
        action_config: selectedApplicationAction.meta || {},
        meta: {
          application_action_id: selectedApplicationAction.id,
        },
      };

      AgentActionService.createAgentAction(
        Number(agentId),
        newAgentAction,
        () => {},
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Agent action created successfully:", response);
          onCapabilityAdded();
          handleModalClose();
          setModalLoading(false);
        },
        (error: any) => {
          console.error("Error creating agent action:", error);
          setModalLoading(false);
        }
      );
    }
  };

  const handleAddAgent = () => {
    if (currentAgent) {
      navigate(
        `/agent/new?application_id=${currentAgent.application_id}&parent_agent_id=${agentId}`
      );
    }
  };

  const handleAddAction = () => {
    navigate(`/agent/${agentId}/actions/new?type=${selectedCapabilityType}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent className="sm:max-w-lg w-auto min-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            Add Capability
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Choose the type of capability you want to add to this agent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-gc-med text-foreground">
              Capability Type
            </label>
            <Select
              value={selectedCapabilityType}
              onValueChange={setSelectedCapabilityType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select capability type" />
              </SelectTrigger>
              <SelectContent className="w-full min-w-[200px]">
                <SelectItem value="api_call">
                  <div className="flex flex-col items-start">
                    <span className="font-gc-med">API Call</span>
                    <span className="text-xs text-muted-foreground">
                      External service integration
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="llm_call">
                  <div className="flex flex-col items-start">
                    <span className="font-gc-med">LLM Call</span>
                    <span className="text-xs text-muted-foreground">
                      AI reasoning and text generation
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="webhook_creation">
                  <div className="flex flex-col items-start">
                    <span className="font-gc-med">Webhook Creation</span>
                    <span className="text-xs text-muted-foreground">
                      Create webhook for async callbacks
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="subagent">
                  <div className="flex flex-col items-start">
                    <span className="font-gc-med">Sub-Agent</span>
                    <span className="text-xs text-muted-foreground">
                      Delegate to another agent
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditional selection based on capability type */}
          {selectedCapabilityType === "subagent" && (
            <div className="space-y-2">
              <label className="text-sm font-gc-med text-foreground">
                Select Agent
              </label>
              {modalLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="text-sm text-muted-foreground">
                    Loading agents...
                  </div>
                </div>
              ) : availableAgents.length > 0 ? (
                <Select
                  value={selectedItem}
                  onValueChange={(value) => {
                    if (value === "new_agent") {
                      handleAddAgent();
                    } else {
                      setSelectedItem(value);
                    }
                  }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select an agent" />
                  </SelectTrigger>
                  <SelectContent className="w-full min-w-[200px]">
                    {availableAgents.map((agent) => (
                      <SelectItem
                        key={agent.id}
                        value={agent.id?.toString() || ""}>
                        {agent.agent_name.length > 50
                          ? `${agent.agent_name.substring(0, 50)}...`
                          : agent.agent_name}
                      </SelectItem>
                    ))}
                    <SelectItem
                      value="new_agent"
                      className="border-t border-border mt-1 pt-2">
                      <div className="flex items-center gap-2 text-maitai-green font-medium">
                        <PlusIcon className="w-4 h-4" />
                        Create New Agent
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="p-4 border border-dashed border-border rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    No agents available in this application
                  </p>
                  <Button
                    onClick={handleAddAgent}
                    variant="outline"
                    size="sm"
                    className="gap-2 border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white">
                    <PlusIcon className="w-4 h-4" />
                    Add Agent
                  </Button>
                </div>
              )}
            </div>
          )}

          {(selectedCapabilityType === "api_call" ||
            selectedCapabilityType === "llm_call") && (
            <div className="space-y-2">
              <label className="text-sm font-gc-med text-foreground">
                Select Action
              </label>
              {modalLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="text-sm text-muted-foreground">
                    Loading actions...
                  </div>
                </div>
              ) : availableActions.length > 0 ? (
                <Select value={selectedItem} onValueChange={setSelectedItem}>
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder="Select an action"
                      className="truncate">
                      {selectedItem &&
                        (() => {
                          const action = availableActions.find(
                            (a) => a.id?.toString() === selectedItem
                          );
                          return action
                            ? `${action.action_type} (ID: ${action.id})`
                            : null;
                        })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="w-full min-w-[200px] max-h-[300px]">
                    {availableActions.map((action) => (
                      <SelectItem
                        key={action.id}
                        value={action.id?.toString() || ""}
                        textValue={`${action.action_type} (ID: ${action.id})`}
                        className="cursor-pointer">
                        <div className="flex flex-col min-w-0">
                          <span className="font-gc-med truncate">
                            {action.action_type}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ID: {action.id}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="p-4 border border-dashed border-border rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    No{" "}
                    {selectedCapabilityType === "api_call"
                      ? "API call"
                      : "LLM call"}{" "}
                    actions available
                  </p>
                  <Button
                    onClick={handleAddAction}
                    variant="outline"
                    size="sm"
                    className="gap-2 border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white">
                    <PlusIcon className="w-4 h-4" />
                    Add Action
                  </Button>
                </div>
              )}
            </div>
          )}

          {selectedCapabilityType &&
            selectedCapabilityType !== "webhook_creation" && (
              <div className="p-3 bg-card border border-border rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {selectedCapabilityType === "api_call" &&
                    "API calls allow your agent to interact with external services and APIs."}
                  {selectedCapabilityType === "llm_call" &&
                    "LLM calls enable your agent to use AI for reasoning, text generation, and analysis."}
                  {selectedCapabilityType === "subagent" &&
                    "Sub-agents allow your agent to delegate specific tasks to other specialized agents."}
                </p>
              </div>
            )}

          {selectedCapabilityType === "webhook_creation" && (
            <div className="space-y-3">
              <div className="p-3 bg-card border border-border rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Webhook creation allows your agent to send an API request that
                  includes a callback URL, then pause execution until the
                  external service calls back.
                </p>
              </div>
              <Button
                onClick={handleAddAction}
                className="w-full gap-2 border-maitai-green bg-maitai-green text-white hover:bg-maitai-green/90">
                <PlusIcon className="w-4 h-4" />
                Create Webhook Action
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleModalClose}>
            Cancel
          </Button>
          {selectedCapabilityType !== "webhook_creation" && (
            <Button
              onClick={() => handleCapabilityTypeSelect(selectedCapabilityType)}
              disabled={!selectedCapabilityType || !selectedItem}
              variant="outline"
              className="border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent disabled:hover:text-muted-foreground">
              Add Capability
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCapabilityModal;
